import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "../lib/api";

function getSessionId() {
  let id = localStorage.getItem("cir_chat_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("cir_chat_session", id);
  }
  return id;
}

const SUGGESTIONS = [
  "How much is a kitchen reno?",
  "Do you build legal suites?",
  "How does your process work?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { id: "welcome", role: "assistant", content: "Hi! I'm Classic AI \ud83d\udc4b Ask me about our renovation services, process, timelines or rough budgets \u2014 or I can help you get a free estimate." },
  ]);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const uid = Date.now().toString(36);
    setMessages((m) => [...m, { id: `u-${uid}`, role: "user", content: msg }, { id: `a-${uid}`, role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  role: "assistant",
                  content: copy[copy.length - 1].content + parsed.delta,
                };
                return copy;
              });
            }
          } catch (err) {
            console.error("Chat SSE parse error:", err);
          }
        }
      }
    } catch (e) {
      console.error("Chat request failed:", e);
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: "Sorry, I had trouble connecting. Please call us at (780) 340-1023." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[90] w-15 h-15 rounded-full bg-[#2f9fe6] text-white shadow-xl shadow-[#2f9fe6]/40 flex items-center justify-center hover:bg-[#1f82c7] transition-all hover:scale-105"
        style={{ width: 60, height: 60 }}
        aria-label="Open chat"
      >
        {open ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
      </button>

      {/* Panel */}
      <div
        className={`fixed bottom-24 right-6 z-[90] w-[calc(100vw-3rem)] sm:w-[380px] h-[560px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-[#e7e9ec] flex flex-col overflow-hidden transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-90 translate-y-4 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-[#0f2740] px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#2f9fe6]/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-[#2f9fe6]" />
          </div>
          <div>
            <div className="text-white font-display font-semibold leading-tight">Classic AI Assistant</div>
            <div className="text-[#7fb8de] text-xs flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Online now
            </div>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f7f9fb]">
          {messages.map((m, i) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#2f9fe6] text-white rounded-br-sm"
                    : "bg-white text-[#33414f] border border-[#eef1f4] rounded-bl-sm"
                }`}
              >
                {m.content || (busy && i === messages.length - 1 ? <TypingDots /> : "")}
              </div>
            </div>
          ))}
        </div>

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2 bg-[#f7f9fb]">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full bg-white border border-[#dfe4e9] text-[#0f2740] text-xs hover:border-[#2f9fe6] hover:text-[#2f9fe6] transition-colors">
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-3 border-t border-[#eef1f4] bg-white flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2.5 rounded-full bg-[#f2f5f8] text-[#33414f] text-sm outline-none focus:ring-2 focus:ring-[#2f9fe6]/40"
          />
          <button
            onClick={() => send()}
            disabled={busy || !input.trim()}
            className="w-11 h-11 rounded-full bg-[#2f9fe6] text-white flex items-center justify-center hover:bg-[#1f82c7] transition-colors disabled:opacity-50 shrink-0"
            aria-label="Send"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex gap-1 py-1">
      <span className="w-2 h-2 rounded-full bg-[#c7ced5] animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-2 h-2 rounded-full bg-[#c7ced5] animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-2 h-2 rounded-full bg-[#c7ced5] animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}
